import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, User, Baby, Calendar, PenTool, Type, RotateCcw } from "lucide-react";

const buildContractText = (sessionLabel) => `IMPORTANT INFORMATION - ${sessionLabel.toUpperCase()} PRIVATE SWIM LESSONS

Drop-Off & Pick-Up:
Private ${sessionLabel} swim lessons are open to all swimmers, no camp enrollment required. A parent/guardian is responsible for getting your child to and from the pool on time for each lesson. Please have your child arrive ready to swim in their bathing suit. Pick-up is directly at the pool when the lesson ends. While you are welcome to watch your child's lesson, please do not interfere with the instructor during the session.

Cancellations, Make-Ups, Refund Policy:
If a family cancels for any reason (i.e. illness, vacation, etc.), our Swim Instructor will do their best to reschedule. However, a make-up lesson is not guaranteed. There are no refunds for missed lessons. If you know your child will miss a lesson, please notify us as soon as possible. Advance absences should be emailed to aquatics@rollingriver.com. Notify the Camp Office directly via phone (516-593-2267) or email (Camp@rollingriver.com) for day-of absences. If we cancel for any reason (i.e. weather, instructor availability, etc.), a make-up lesson will be scheduled or a refund for the missed lesson will be provided. Communication regarding canceled lessons will be through email, text, and/or phone. Your instructor will reach out to you directly to schedule a make-up lesson.

Agreement, Waiver, and Release:
I, the Parent, have carefully read the description of Lessons for which we are registering, and, in consideration for being permitted by the Instructor to participate in the activity, I hereby waive, release, and discharge any and all claims for damages for personal injury, death, or property damage which I may have, or which may hereafter inure to me, as a result of participation in Lessons. I hereby acknowledge that participation in Lessons does not guarantee that my child will be water-safe. This release is intended to discharge in advance the Instructor and Rolling River Day Camp from any and all liability arising out of, or connected in any way, with my participation in the Lessons, even though that liability may arise out of negligence or carelessness on the part of Instructor. It is understood that this Activity involves an element of risk and danger of accidents, and knowing those risks, I hereby assume those risks. It is further agreed that this waiver, release and assumption of risk is to be binding on my heirs and assigns. I agree to indemnify and to hold Instructors and Rolling River Day Camp free and harmless from any loss, liability, damage, cost, or expense which they may incur as the result of my or my child's injury or property damage that I may sustain while participating in the Activity.`;

const API = process.env.REACT_APP_BACKEND_URL;

const SESSION_LABELS = { june: "June (Pre Season)", july_august: "July-August (In Season)" };

export default function NonEnrolledRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawSession = searchParams.get("session") || "june";
  const session = ["june", "july_august"].includes(rawSession) ? rawSession : "june";
  const sessionLabel = SESSION_LABELS[session];

  const sigCanvas = useRef(null);
  const [loading, setLoading] = useState(false);
  const [lessonSlots, setLessonSlots] = useState([]);
  const [duplicateError, setDuplicateError] = useState(null);
  const [signatureType, setSignatureType] = useState("canvas");
  const [typedSignature, setTypedSignature] = useState("");

  const [formData, setFormData] = useState({
    child_first_name: "", child_last_name: "", child_dob: "",
    parent_first_name: "", parent_last_name: "",
    phone: "", email: "", address: "", city: "", state: "", zip_code: "",
    swim_level: "", choice1_day: "", choice1_time: "", choice1_lifeguard_id: "",
    notes: ""
  });

  // Block July-August route if disabled
  useEffect(() => {
    let mounted = true;
    if (session === "july_august") {
      axios.get(`${API}/api/non-enrolled/session-availability`).then(r => {
        if (!mounted) return;
        if (!r.data.july_august) {
          toast.error("July-August registration is not yet open.");
          navigate("/non-enrolled", { replace: true });
        }
      }).catch(() => {});
    }
    return () => { mounted = false; };
  }, [session, navigate]);

  useEffect(() => {
    axios.get(`${API}/api/non-enrolled/lesson-slots?session=${session}`).then(r => setLessonSlots(r.data)).catch(() => {});
  }, [session]);

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const lifeguards = [...new Map(lessonSlots.map(s => [s.lifeguard_id, { id: s.lifeguard_id, name: s.lifeguard_name }])).values()];

  const availableDays = [...new Set(
    lessonSlots
      .filter(s => s.lifeguard_id === formData.choice1_lifeguard_id)
      .map(s => s.day_of_week)
  )].filter(d => session !== "july_august" || d !== "saturday");

  const availableTimes = lessonSlots
    .filter(s => s.lifeguard_id === formData.choice1_lifeguard_id && s.day_of_week === formData.choice1_day)
    .map(s => ({ time: s.time, price: s.price }));

  const selectedSlot = lessonSlots.find(
    s => s.lifeguard_id === formData.choice1_lifeguard_id && s.day_of_week === formData.choice1_day && s.time === formData.choice1_time
  );

  const clearSignature = () => { if (sigCanvas.current) sigCanvas.current.clear(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.child_first_name || !formData.child_last_name || !formData.parent_first_name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!formData.choice1_lifeguard_id || !formData.choice1_day || !formData.choice1_time) {
      toast.error("Please select a lesson slot.");
      return;
    }
    // Require signature BEFORE creating any registration
    let signature = "";
    if (signatureType === "canvas") {
      if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
        toast.error("Please sign the contract to complete booking.");
        return;
      }
      signature = sigCanvas.current.toDataURL();
    } else {
      if (!typedSignature.trim()) {
        toast.error("Please type your signature to complete booking.");
        return;
      }
      signature = typedSignature.trim();
    }

    setLoading(true);
    setDuplicateError(null);
    try {
      // 1. Register (creates tentative) — include session
      const res = await axios.post(`${API}/api/non-enrolled/register`, { ...formData, session });
      const regId = res.data.registration_id;
      // 2. Sign immediately so status becomes "signed" before parent ever sees confirmation
      await axios.post(`${API}/api/non-enrolled/sign-contract`, {
        registration_id: regId,
        signature,
        signature_type: signatureType,
      });
      toast.success("Lesson booked and contract signed!");
      navigate(`/non-enrolled/confirmation/${regId}`);
    } catch (err) {
      if (err.response?.status === 409) {
        setDuplicateError(err.response.data.detail);
      } else {
        toast.error(err.response?.data?.detail || "Registration failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="p-4">
        <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </div>

      <div className="max-w-3xl mx-auto px-4 pb-12">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="https://customer-assets.emergentagent.com/job_pool-lesson-book/artifacts/c9s2nl0s_rr%20logo.png" alt="RRDC" className="w-20 h-20 mx-auto mb-3" />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#2B4C8C]">{sessionLabel} Lessons</h1>
          <p className="font-body text-slate-600 mt-2 text-lg">Open To Anyone</p>
        </div>

        {/* Top Disclaimer */}
        <div className="disclaimer-box mb-8" data-testid="ne-disclaimer-box">
          <p className="disclaimer-text">
            {session === "july_august"
              ? "Private swim lessons are open to all swimmers, no camp enrollment required. Lessons are offered Monday Through Friday during the months of July and August (excluding July 3 and July 4). Registration is completed online, and payment is due in cash to the camp office within 7 days of registration or your selection will be cancelled."
              : "Private swim lessons are open to all swimmers, no camp enrollment required. Lessons are offered Monday Through Friday during the month of June. Registration is completed online, and payment is due in cash to the camp office within 7 days of registration or your selection will be cancelled."}
          </p>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="card space-y-8 animate-fade-in">
          {/* Parent Information */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#D4A843] mb-6 flex items-center gap-2">
              <User className="w-6 h-6" /> Parent Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">First Name *</Label>
                <Input data-testid="ne-parent-first" value={formData.parent_first_name} onChange={e => update("parent_first_name", e.target.value)} required className="form-input" />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Last Name *</Label>
                <Input data-testid="ne-parent-last" value={formData.parent_last_name} onChange={e => update("parent_last_name", e.target.value)} required className="form-input" />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Email *</Label>
                <Input data-testid="ne-email" type="email" value={formData.email} onChange={e => update("email", e.target.value)} required className="form-input" />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Phone *</Label>
                <Input data-testid="ne-phone" value={formData.phone} onChange={e => update("phone", e.target.value)} required className="form-input" />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Address</Label>
                <Input value={formData.address} onChange={e => update("address", e.target.value)} className="form-input" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">City</Label>
                  <Input value={formData.city} onChange={e => update("city", e.target.value)} className="form-input" />
                </div>
                <div>
                  <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">State</Label>
                  <Input value={formData.state} onChange={e => update("state", e.target.value)} className="form-input" />
                </div>
                <div>
                  <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Zip</Label>
                  <Input value={formData.zip_code} onChange={e => update("zip_code", e.target.value)} className="form-input" />
                </div>
              </div>
            </div>
          </section>

          {/* Child Information */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#D4A843] mb-6 flex items-center gap-2">
              <Baby className="w-6 h-6" /> Child Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">First Name *</Label>
                <Input data-testid="ne-child-first" value={formData.child_first_name} onChange={e => update("child_first_name", e.target.value)} required className="form-input" />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Last Name *</Label>
                <Input data-testid="ne-child-last" value={formData.child_last_name} onChange={e => update("child_last_name", e.target.value)} required className="form-input" />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Date of Birth</Label>
                <Input data-testid="ne-child-dob" type="date" value={formData.child_dob} onChange={e => update("child_dob", e.target.value)} className="form-input" />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Swim Level</Label>
                <Select value={formData.swim_level} onValueChange={v => update("swim_level", v)}>
                  <SelectTrigger data-testid="ne-swim-level" className="form-input"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate">Intermediate</SelectItem>
                    <SelectItem value="Advanced">Advanced</SelectItem>
                    <SelectItem value="Unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Additional Information</Label>
              <Textarea
                data-testid="ne-notes"
                className="form-input w-full min-h-[80px]"
                placeholder="Any additional information about your child (allergies, special needs, notes for the instructor, etc.)"
                value={formData.notes}
                onChange={e => update("notes", e.target.value)}
              />
            </div>
            <p className="text-sm text-slate-500 mt-4 italic">
              If you are unsure about which instructor to select please reach out to our aquatics supervisor at{" "}
              <a href="mailto:aquatics@rollingriver.com" className="text-[#D4A843] underline">aquatics@rollingriver.com</a>.
            </p>
          </section>

          {/* Lesson Selection */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#D4A843] mb-6 flex items-center gap-2">
              <Calendar className="w-6 h-6" /> Select Lesson
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Instructor *</Label>
                <Select value={formData.choice1_lifeguard_id} onValueChange={v => { update("choice1_lifeguard_id", v); update("choice1_day", ""); update("choice1_time", ""); }}>
                  <SelectTrigger data-testid="ne-lifeguard" className="form-input"><SelectValue placeholder="Select instructor" /></SelectTrigger>
                  <SelectContent>
                    {lifeguards.map(lg => (
                      <SelectItem key={lg.id} value={lg.id}>{lg.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.choice1_lifeguard_id && (
                <div>
                  <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Day *</Label>
                  <Select value={formData.choice1_day} onValueChange={v => { update("choice1_day", v); update("choice1_time", ""); }}>
                    <SelectTrigger data-testid="ne-day" className="form-input"><SelectValue placeholder="Select day" /></SelectTrigger>
                    <SelectContent>
                      {availableDays.map(d => (
                        <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formData.choice1_day && (
                <div>
                  <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">Time *</Label>
                  <Select value={formData.choice1_time} onValueChange={v => update("choice1_time", v)}>
                    <SelectTrigger data-testid="ne-time" className="form-input"><SelectValue placeholder="Select time" /></SelectTrigger>
                    <SelectContent>
                      {availableTimes.map(t => (
                        <SelectItem key={t.time} value={t.time}>{t.time} — ${t.price}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {selectedSlot && (
                <div className="bg-[#FDF6E3] border-2 border-[#D4A843] rounded-xl p-4">
                  <p className="font-bold text-[#92400E]">
                    {selectedSlot.day_of_week.charAt(0).toUpperCase() + selectedSlot.day_of_week.slice(1)}s at {selectedSlot.time}
                  </p>
                  <p className="text-slate-600 text-sm">Instructor: {selectedSlot.lifeguard_name}</p>
                  {selectedSlot.lesson_count ? (
                    <p className="text-slate-700 text-sm mt-1">
                      {selectedSlot.lesson_count} lesson{selectedSlot.lesson_count !== 1 ? "s" : ""} × ${selectedSlot.price_per_lesson || (selectedSlot.price / selectedSlot.lesson_count).toFixed(2)} = <span className="font-bold text-[#2B4C8C]">${selectedSlot.price} total</span>
                    </p>
                  ) : (
                    <p className="text-slate-700 text-sm mt-1">Total: <span className="font-bold text-[#2B4C8C]">${selectedSlot.price}</span></p>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Cancellation Disclaimer */}
          <div className="bg-[#FDF6E3] rounded-xl p-4" data-testid="ne-cancellation-disclaimer">
            <p className="text-sm text-black font-bold text-center" style={{ fontSize: '14px' }}>
              Please carefully review your selected date and time to ensure it works for both you and your child. Cancellations initiated by the family cannot be guaranteed a reschedule. In the event that camp or the instructor must cancel a session, we will make every effort to find a suitable alternative time.
            </p>
          </div>

          {/* Payment Warning */}
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 text-center" data-testid="ne-payment-warning">
            <p className="text-lg font-bold text-red-700">
              Payment is due within 7 days or your lesson will be cancelled.
            </p>
          </div>

          {duplicateError && (
            <div className="bg-[#FEF2F2] border border-red-300 rounded-xl p-4" data-testid="ne-duplicate-error">
              <p className="text-sm text-red-700 font-semibold text-center">{duplicateError}</p>
            </div>
          )}

          {/* ===== Contract + Signature (inline; must sign to book) ===== */}
          <section className="space-y-4 border-t pt-6">
            <h2 className="font-heading text-xl font-bold text-[#2B4C8C]">Contract & Signature</h2>
            <p className="text-sm text-slate-600">
              You must read and sign the contract below to complete your booking. Your slot is not held until you sign.
            </p>

            <div className="bg-slate-50 rounded-xl p-4 max-h-72 overflow-y-auto" data-testid="ne-contract-text">
              <pre className="whitespace-pre-wrap font-body text-xs sm:text-sm text-slate-700 leading-relaxed">
                {buildContractText(sessionLabel)}
              </pre>
            </div>

            <div>
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 block">Parent Signature *</Label>
              <Tabs value={signatureType} onValueChange={setSignatureType} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-3">
                  <TabsTrigger value="canvas" className="flex items-center gap-2">
                    <PenTool className="w-4 h-4" /> Draw
                  </TabsTrigger>
                  <TabsTrigger value="typed" className="flex items-center gap-2">
                    <Type className="w-4 h-4" /> Type
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="canvas">
                  <div className="relative">
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: "signature-pad w-full",
                        "data-testid": "ne-signature-canvas",
                      }}
                      backgroundColor="#f8fafc"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearSignature}
                      className="absolute top-2 right-2"
                      data-testid="ne-clear-signature-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Clear
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Draw your signature above with your mouse or touchscreen.
                  </p>
                </TabsContent>

                <TabsContent value="typed">
                  <Input
                    data-testid="ne-typed-signature-input"
                    className="form-input w-full text-2xl"
                    style={{ fontFamily: "'Dancing Script', cursive, serif" }}
                    placeholder="Your Full Name"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                  />
                </TabsContent>
              </Tabs>
            </div>

            <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-3 text-xs text-yellow-800">
              By signing above, I, <b>{formData.parent_first_name} {formData.parent_last_name}</b>, agree to the terms in the contract.
            </div>
          </section>

          <Button type="submit" disabled={loading} className="w-full py-4 bg-[#D4A843] hover:bg-[#b8902e] text-white text-lg font-bold rounded-xl" data-testid="ne-submit-btn">
            {loading ? "Booking..." : "Sign & Book Lesson"}
          </Button>
        </form>
      </div>
    </div>
  );
}
